import React, { Component } from 'react';
import Cursor from '../../../../../../../../../../../css/ppt-editor-cursor-global.less';
import { mobxSet } from '@src/slide/infrastructure/module/independent/assert/get-set';
import { pointDirectionForSeg, PointToSegDirection } from '@src/slide/infrastructure/module/independent/graphic';
import { computeAngle, computeAngleReaminder, computeOffsetVariables, computeAdsorpOffset, ADSORP_ANGLE, ADSORP_RANGE } from '@src/slide/infrastructure/module/independent/graphic/rotate';
import _get from 'lodash/get';
import * as commonFun from '../../../../common';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { ServicesContext } from '../../../context';
import { IConnectorModel } from '@src/slide/domain/ui-model/element-modeles/Interface/connector-model.interface';
import { IEditorAppServices } from '@src/slide/application/entry/toc/editor/base/base-editor-app-service.interface';

export interface ConnectorStartEndPointProps {
    element: IConnectorModel;
    scale: number;
}

export default class ConnectorStartEndPoint<P extends ConnectorStartEndPointProps> extends Component<P> {
    public static contextType = ServicesContext;
    protected startPos;
    protected changeStartEndIndex;
    public render() {
        const frame = this.props.element.getFrame?.().getFrameInfo(true);

        const startAndEndPoints = [{
            x: 0,
            y: 0,
        }, {
            x: frame.width,
            y: frame.height,
        },
        ];
        return (
            <div
                style={{
                    width: frame.width,
                    height: frame.height,
                    position: 'absolute',
                    transform: commonFun.transformOfFlip(frame),
                    pointerEvents: 'none',
                }}
            >
                {
                    startAndEndPoints.map(this.renderStartAndEndPoint)
                }
            </div>
        );
    }

    protected handleStartEndPointMouseDown = (index, e) => {
        e.stopPropagation();
        e.preventDefault();
        this.startPos = this.getEventCoordinates(e);
        this.changeStartEndIndex = index;
        document.addEventListener('mousemove', this.handleStartEndPointMouseMove);
        document.addEventListener('mouseup', this.handleStartEndPointChangeEnd);
        document.addEventListener('touchmove', this.handleStartEndPointMouseMove, { passive: false });
        document.addEventListener('touchend', this.handleStartEndPointChangeEnd, { passive: false });
    };

    protected getEventCoordinates = e => ({
        x: e.touches ? e.touches[0].clientX : e.clientX,
        y: e.touches ? e.touches[0].clientY : e.clientY,
    });

    protected handleStartEndPointMouseMove = (e) => {
        e.stopPropagation();
        e.preventDefault();
        if (!this.context.editingStateManager.isConnecting) {
            this.context.editingStateManager.connectingInfo = {
                model: this.props.element,
                endIndex: this.changeStartEndIndex,
            };
            this.context.editingStateManager.isConnecting = true;
            if (this.changeStartEndIndex === 0) {
                // 如果连接的图形也没有被选中，需要被置空
                const startModel = this.props.element.startUIModel;
                if (startModel) {
                    this.props.element.props.style.connectorInfo.start = null;
                    startModel.removeRelatedUIModelRefs(this.props.element);
                }
            } else if (this.changeStartEndIndex === 1) {
                // 如果连接的图形也没有被选中，需要被置空
                const endModel = this.props.element.endUIModel;
                if (endModel) {
                    this.props.element.props.style.connectorInfo.end = null;
                    endModel.removeRelatedUIModelRefs(this.props.element);
                }
            }
        }
        const currentEventPos = this.getEventCoordinates(e);
        // 计算出相对与屏幕的偏移
        const realOffset = {
            x: (currentEventPos.x - this.startPos.x) / this.props.scale,
            y: (currentEventPos.y - this.startPos.y) / this.props.scale,
        };
        let offset;
        offset = this.getAdsorpOffset(ADSORP_RANGE, ADSORP_ANGLE, {
            endIndex: this.changeStartEndIndex,
            offset: realOffset,
        });
        if (e.shiftKey) {
            offset = this.sameRatioOffset({
                endIndex: this.changeStartEndIndex,
                offset,
            });
        }
        const updateSytle = this.props.element.connectorCaculater?.getUpdateStyle(null, {
            endIndex: this.changeStartEndIndex,
            offset,
        });
        this.props.element.updateModelStyle(updateSytle);
    };

    protected getAdsorpOffset(adsorpRange, adsorpAngle, offsetInfo) {
        let renderOffset = offsetInfo.offset;
        const frame = this.props.element.getFrame?.().getFrameInfo(false);
        const startEndPoint = this.props.element.getStartEndPoints(frame); // 开始点和结束点位置
        const { startP } = startEndPoint; // 开始点
        const { endP } = startEndPoint; // 结束点
        let currentP;
        let fixP;
        let moveP;
        const points = this.props.element.getStartEndPoints(frame, offsetInfo); // 加上偏移之后的鼠标的位置
        if (offsetInfo.endIndex === 0) { // 如果移动的是开始点
            currentP = points.startP;
            fixP = endP;
            moveP = startP;
        } else {
            currentP = points.endP;
            fixP = startP;
            moveP = endP;
        }
        const angle = computeAngle(currentP, fixP);
        const rotateAngleRemainder = computeAngleReaminder(angle, adsorpAngle, adsorpRange);
        // 是否需要吸附
        const isAdsorp = Math.abs(rotateAngleRemainder) <= adsorpRange;
        if (isAdsorp) {
            const { x, y } = computeOffsetVariables(
                currentP,
                fixP,
                adsorpAngle,
                adsorpRange,
            );
            renderOffset = computeAdsorpOffset(fixP, moveP, x, y);
        }
        return renderOffset;
    }

    protected handleStartEndPointChangeEnd = (e) => {
        e.stopPropagation();
        e.preventDefault();
        document.removeEventListener('mousemove', this.handleStartEndPointMouseMove);
        document.removeEventListener('mouseup', this.handleStartEndPointChangeEnd);
        document.removeEventListener('touchmove', this.handleStartEndPointMouseMove);
        document.removeEventListener('touchend', this.handleStartEndPointChangeEnd);
        const styleModel = this.props.element.props.style;
        const toRemove = this.props.element.toRemoveConnectorInfoProps();
        const updateProps = {
            style: {
                ...this.props.element.operationFrame,
                bgLine: styleModel.bgLine,
                controlValues: styleModel.controlValues,
                connectorInfo: this.props.element.props.style.connectorInfo,
            },
        };
        if (toRemove) {
            this.props.element.updateAndRemoveProps(updateProps, toRemove);
        } else {
            this.props.element.updateProps(updateProps);
        }
        mobxSet(this.props.element, 'operationFrame', null);
        if (this.context.editingStateManager.isConnecting) {
            this.context.editingStateManager.isConnecting = false;
            this.context.editingStateManager.connectingInfo = null;
        }
        if (this.props.element.getConnectorInfo()) {
            this.props.element.buildRelation();
        }
    };

    protected sameRatioOffset(offsetInfo) {
        let currentOffset;
        const frame = this.props.element.getFrame?.().getFrameInfo(false);
        const startEndPoint = this.props.element.getStartEndPoints(frame); // 开始点和结束点位置
        const { startP } = startEndPoint; // 开始点
        const { endP } = startEndPoint; // 结束点
        let currentP = {
            x: 0,
            y: 0,
        };
        const points = this.props.element.getStartEndPoints(frame, offsetInfo); // 加上偏移之后的鼠标的位置
        const maxOffset = {
            x: this.getUiDataService().presentation.props.slideWidth,
            y: this.getUiDataService().presentation.props.slideHeight,
        };
        if (offsetInfo.endIndex === 0) { // 如果移动的是开始点
            currentP = points.startP;
        } else {
            currentP = points.endP;
        }
        let toHeight = true;
        if ((_get(frame, 'flipH') === 1 && _get(frame, 'flipV') !== 1)
            || (_get(frame, 'flipH') !== 1 && _get(frame, 'flipV') === 1)) {
            toHeight = !toHeight;
        }
        const pToSegDirection = pointDirectionForSeg(currentP, startP, endP);
        if (pToSegDirection === PointToSegDirection.LEFT) {
            toHeight = !toHeight;
        }
        currentOffset = this.offsetWithFrame(frame, offsetInfo, toHeight);
        if (Math.abs(currentOffset.x) > maxOffset.x || Math.abs(currentOffset.y) > maxOffset.y) {
            currentOffset = this.offsetWithFrame(frame, offsetInfo, !toHeight);
        }
        return currentOffset;
    }

    protected applyInfoOnOffset(frame, offset, revert?) {
        const currentOffset = offset;
        if (frame.flipH && frame.flipH === 1) {
            currentOffset.x = -currentOffset.x;
        }
        if (frame.flipV && frame.flipV === 1) {
            currentOffset.y = -currentOffset.y;
        }
        if (frame.rotate && frame.rotate !== 0) {
            let angle = (frame.rotate % 360) * Math.PI / 180;
            if (!revert) {
                angle = -angle;
            }
            currentOffset.x = currentOffset.x * Math.cos(angle);
            currentOffset.y = currentOffset.y * Math.sin(angle);
        }
    }

    protected offsetWithFrame(frame, offsetInfo, toHeight) {
        let currentOffset = {
            x: offsetInfo.offset.x,
            y: offsetInfo.offset.y,
        };
        this.applyInfoOnOffset(frame, currentOffset);
        if (frame.height === 0) {
            currentOffset.y = 0;
        } else if (frame.width === 0) {
            currentOffset.x = 0;
        } else if (toHeight) {
            currentOffset = {
                x: currentOffset.y * frame.width / frame.height,
                y: currentOffset.y,
            };
        } else {
            currentOffset = {
                x: currentOffset.x,
                y: currentOffset.x * frame.height / frame.width,
            };
        }
        this.applyInfoOnOffset(frame, currentOffset, true);
        return currentOffset;
    }

    protected renderStartAndEndPoint = (point, index) => {
        const { scale } = this.props;
        return (
            <div
                key={index}
                style={{
                    left: point.x - 5,
                    top: point.y - 5,
                    position: 'absolute',
                    pointerEvents: 'auto',
                    backgroundColor: '#0188fb',
                    height: 10,
                    width: 10,
                    borderRadius: '100%',
                    border: '1px solid #fff',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.32)',
                    transform: `scale(${1 / scale})`,
                }}
                className={Cursor['nwse-resize']}
                onMouseDown={this.handleStartEndPointMouseDown.bind(this, index)}
                onTouchStart={this.handleStartEndPointMouseDown.bind(this, index)}
            >
                {
                    // 移动端扩大可触摸范围
                    browser.isMobile ? <div
                        style={{
                            height: 20,
                            width: 20,
                            left: -5,
                            top: -5,
                            position: 'absolute',
                        }}
                    >
                    </div> : null
                }
            </div>
        );
    };

    protected getUiDataService() {
        return (AppServiceProvider.getInstance().getAppServices() as IEditorAppServices)
            .getUiDataService();
    }
}
